import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { describeSupabaseError, isEmbedded, supabase } from '../utils/supabase';
import { hashMasterPassword, readUnlockFlag, writeUnlockFlag } from '../utils/security';
const MASTER_HASH_KEY = 'master_password_hash';
interface AuthContextValue {
  user: User | null;
  session: Session | null;
  initializing: boolean;
  hasMasterPassword: boolean;
  unlocked: boolean;
  embedded: boolean;
  signInError: string | null;
  clearSignInError: () => void;
  signInWithGoogle: () => Promise<void>;
  createMasterPassword: (password: string) => Promise<void>;
  unlockWithMasterPassword: (password: string) => Promise<boolean>;
  changeMasterPassword: (current: string, next: string) => Promise<void>;
  lock: () => void;
  logout: () => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const embedded = isEmbedded();
  const user = session?.user ?? null;
  // The digest travels with the Supabase account, so any device that signs in
  // resolves to the same account instead of looking brand new.
  const storedHash = (user?.user_metadata?.[MASTER_HASH_KEY] as string | undefined ?? null) || null;
  const hasMasterPassword = Boolean(storedHash);
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({
      data,
      error
    }) => {
      if (!active) return;
      if (error) setSignInError(describeSupabaseError(error));
      setSession(data.session ?? null);
    }).catch(error => {
      if (active) setSignInError(describeSupabaseError(error));
    }).finally(() => {
      if (active) setInitializing(false);
    });
    const {
      data
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setInitializing(false);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  // Re-evaluate the lock whenever the account (or its digest) changes.
  useEffect(() => {
    if (!user || !storedHash) {
      setUnlocked(false);
      return;
    }
    setUnlocked(readUnlockFlag(user.id));
  }, [user?.id, storedHash]);
  const signInWithGoogle = useCallback(async () => {
    setSignInError(null);
    const {
      error
    } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/security`,
        queryParams: {
          prompt: 'select_account'
        }
      }
    });
    if (error) {
      setSignInError(describeSupabaseError(error));
      throw error;
    }
  }, []);
  const clearSignInError = useCallback(() => setSignInError(null), []);
  const saveHash = useCallback(async (hash: string) => {
    const {
      error
    } = await supabase.auth.updateUser({
      data: {
        [MASTER_HASH_KEY]: hash
      }
    });
    if (error) throw new Error(describeSupabaseError(error));
    const {
      data
    } = await supabase.auth.getSession();
    setSession(data.session ?? null);
  }, []);
  const createMasterPassword = useCallback(async (password: string) => {
    if (!user) throw new Error('You must be signed in first.');
    const hash = await hashMasterPassword(user.id, password);
    await saveHash(hash);
    writeUnlockFlag(user.id, true);
    setUnlocked(true);
  }, [saveHash, user]);
  const unlockWithMasterPassword = useCallback(async (password: string) => {
    if (!user || !storedHash) return false;
    const hash = await hashMasterPassword(user.id, password);
    if (hash !== storedHash) return false;
    writeUnlockFlag(user.id, true);
    setUnlocked(true);
    return true;
  }, [storedHash, user]);
  const changeMasterPassword = useCallback(async (current: string, next: string) => {
    if (!user) throw new Error('You must be signed in first.');
    const currentHash = await hashMasterPassword(user.id, current);
    if (currentHash !== storedHash) throw new Error('Your current master password is incorrect.');
    const nextHash = await hashMasterPassword(user.id, next);
    await saveHash(nextHash);
    writeUnlockFlag(user.id, true);
  }, [saveHash, storedHash, user]);
  const lock = useCallback(() => {
    if (user) writeUnlockFlag(user.id, false);
    setUnlocked(false);
  }, [user]);
  const logout = useCallback(async () => {
    if (user) writeUnlockFlag(user.id, false);
    setUnlocked(false);
    const {
      error
    } = await supabase.auth.signOut();
    if (error) throw new Error(describeSupabaseError(error));
    setSession(null);
  }, [user]);
  const value = useMemo<AuthContextValue>(() => ({
    user,
    session,
    initializing,
    hasMasterPassword,
    unlocked,
    embedded,
    signInError,
    clearSignInError,
    signInWithGoogle,
    createMasterPassword,
    unlockWithMasterPassword,
    changeMasterPassword,
    lock,
    logout
  }), [user, session, initializing, hasMasterPassword, unlocked, embedded, signInError, clearSignInError, signInWithGoogle, createMasterPassword, unlockWithMasterPassword, changeMasterPassword, lock, logout]);
  return <AuthContext.Provider value={value} data-id="element-485" data-mp="cclcy06nmb1l">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}